import { useT } from '@/core/I18nProvider';

import { BEAT, enter } from './branch-beats';
import { AnswerBody, ChartBody, ShipmentBody } from './branch-bodies-2';
import { PolicyBody } from './branch-body-policy';
import { CardTitle, Spacer } from './branch-parts';

import type { Branch } from './story-data';
import type { ReactElement } from 'react';

/**
 * The six result-card bodies.
 *
 * Each is a DIFFERENT interface, which is the band's entire argument: nobody
 * wrote any of these six screens, and they are not six skins on one list. The
 * shapes are drawn from the product's real composition vocabulary — a picker,
 * a reorderable plan, a governance draft, a chart, a cited answer, a progress
 * tracker — so the demo never shows a layout the product could not actually
 * compose.
 *
 * Timing is derived from the branch's `at` rather than hand-authored per
 * element. Six branches × ~10 animated parts is 60 delays; authoring them by
 * hand is how a choreography drifts out of sync one branch at a time.
 */

export function BranchCard({ branch }: { readonly branch: Branch }): ReactElement {
  switch (branch.body.kind) {
    case 'products':
      return <ProductsBody branch={branch} />;
    case 'itinerary':
      return <ItineraryBody branch={branch} />;
    case 'policy':
      return <PolicyBody branch={branch} />;
    case 'chart':
      return <ChartBody branch={branch} />;
    case 'answer':
      return <AnswerBody branch={branch} />;
    case 'shipment':
      return <ShipmentBody branch={branch} />;
  }
}

/* ------------------------------------------------------------------ */
/* 1 · Products — a picker that resolves into a cart line              */
/* ------------------------------------------------------------------ */

const PRODUCTS = [
  { name: 'RTX 4070', price: '$549', art: 'linear-gradient(140deg,#1e293b,#3b4a63)', pick: false },
  {
    name: 'RTX 4080',
    price: '$999',
    art: 'linear-gradient(140deg,var(--bd-blue),var(--bd-purple))',
    pick: true,
  },
  { name: 'RX 7800', price: '$499', art: 'linear-gradient(140deg,#334155,#4b5a70)', pick: false },
];

function ProductsBody({ branch }: { readonly branch: Branch }): ReactElement {
  const { t } = useT();
  const { at } = branch;
  return (
    <>
      <CardTitle at={at}>{t('home.story.products.title')}</CardTitle>
      <div className="relative mt-3 flex flex-none gap-2">
        {PRODUCTS.map((product, i) => (
          <div
            key={product.name}
            className={[
              'relative flex min-w-0 flex-1 flex-col gap-[7px] rounded-[11px] p-[7px]',
              product.pick
                ? 'border-[1.5px] border-[#2563eb] bg-[rgba(37,99,235,.035)]'
                : 'border-[1.5px] border-[#e7eaef] bg-white',
            ].join(' ')}
            style={{
              boxShadow: product.pick
                ? '0 0 0 3px rgba(37,99,235,.1), 0 12px 24px -16px rgba(37,99,235,.38)'
                : undefined,
              animation: enter(at, BEAT.body + i * 0.12),
            }}
          >
            {product.pick ? (
              <>
                <span
                  aria-hidden="true"
                  className="absolute start-1/2 top-8 -ms-[13px] size-[26px] rounded-full border-2 border-[#2563eb] opacity-0"
                  style={{ animation: `sxPing 1s ease-out ${(at + 3.25).toFixed(2)}s backwards` }}
                />
                <span
                  aria-hidden="true"
                  className="absolute -top-2 -end-2 grid size-5 place-items-center rounded-full bg-[#2563eb] text-[10px] text-white"
                  style={{
                    boxShadow: '0 5px 12px -4px rgba(37,99,235,.38)',
                    animation: `sxStamp .5s cubic-bezier(.34,1.56,.64,1) ${(at + 3.55).toFixed(2)}s backwards`,
                  }}
                >
                  ✓
                </span>
              </>
            ) : null}
            <span className="block h-[52px] rounded-lg" style={{ background: product.art }} />
            <span className="truncate text-[11px] leading-tight text-[#5f6673]">
              {product.name}
            </span>
            <span className="text-[13px] font-semibold tracking-[-0.01em] text-[#0b0f1a]">
              {product.price}
            </span>
          </div>
        ))}
      </div>
      <Spacer />
      <div
        className="flex flex-none items-center gap-2.5 rounded-xl border border-[rgba(4,120,87,.16)] bg-[rgba(4,120,87,.05)] px-3 py-2.5"
        style={{ animation: enter(at, BEAT.settle + 0.55, 'sxUp', '.6s') }}
      >
        <span
          aria-hidden="true"
          className="grid size-[18px] flex-none place-items-center rounded-full bg-[#047857] text-[9px] text-white"
        >
          ✓
        </span>
        <span className="min-w-0 flex-1 text-[12.5px] text-[#22503f]">
          {t('home.story.products.added')}
        </span>
        <span className="whitespace-nowrap text-[13px] font-semibold text-[#22503f]">$999</span>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* 2 · Itinerary — a picker that SWAPS into a day plan                 */
/* ------------------------------------------------------------------ */

const CITIES = [
  { key: 'tokyo', kanji: '東京', art: 'linear-gradient(140deg,#1D4ED8,#0E7490)', pick: true },
  { key: 'kyoto', kanji: '京都', art: 'linear-gradient(140deg,#6D28D9,#8B0FB0)', pick: false },
  { key: 'osaka', kanji: '大阪', art: 'linear-gradient(140deg,#047857,#0E7490)', pick: false },
];

function ItineraryBody({ branch }: { readonly branch: Branch }): ReactElement {
  const { t } = useT();
  const { at } = branch;
  // The one branch whose card replaces its own body in place: the visitor sees
  // a choice resolve into a plan, which is a thing only a composed surface can
  // do — a static screenshot could show either state but never the transition.
  const swapAt = at + 4.0;
  return (
    <div className="relative mt-3 flex-1">
      <div
        className="absolute inset-0 flex flex-col gap-2.5"
        style={{ animation: `sxOut .45s ease ${swapAt.toFixed(2)}s forwards` }}
      >
        {CITIES.map((city, i) => (
          <div
            key={city.key}
            className={[
              'flex items-center gap-2.5 rounded-[11px] py-[7px] pe-2.5 ps-[7px]',
              city.pick
                ? 'border-[1.5px] border-[#0891b2] bg-[rgba(8,145,178,.04)]'
                : 'border-[1.5px] border-[#e7eaef] bg-white',
            ].join(' ')}
            style={{
              boxShadow: city.pick ? '0 0 0 3px rgba(8,145,178,.09)' : undefined,
              animation: enter(at, BEAT.body + 0.2 + i * 0.12),
            }}
          >
            <span
              className="grid h-[34px] w-11 flex-none place-items-center rounded-lg text-[12.5px] font-semibold tracking-wide text-white/90"
              style={{ background: city.art }}
              // The tiles are place names in their own script — content, not UI
              // copy — so they are never translated and never transliterated.
              //
              // rtl-tracking-ok: the `lang` below is the reason. Japanese has
              // no cursive joining and no letter case, so the tracking is a
              // typographic choice here rather than the Arabic hazard.
              lang="ja"
            >
              {city.kanji}
            </span>
            <span className="flex min-w-0 flex-1 flex-col leading-tight">
              <span className="text-[13px] font-semibold text-[#0b0f1a]">
                {t(`home.story.itinerary.${city.key}`)}
              </span>
              <span className="truncate text-[11px] text-[#5f6673]">
                {t(`home.story.itinerary.${city.key}Desc`)}
              </span>
            </span>
            {city.pick ? (
              <span
                aria-hidden="true"
                className="grid size-[19px] flex-none place-items-center rounded-full bg-[#0e7490] text-[9px] text-white"
                style={{
                  animation: `sxStamp .45s cubic-bezier(.34,1.56,.64,1) ${(at + 3.55).toFixed(2)}s backwards`,
                }}
              >
                ✓
              </span>
            ) : null}
          </div>
        ))}
      </div>

      <div
        className="absolute inset-0 flex flex-col gap-2 opacity-0"
        style={{
          animation: `sxSwap .6s cubic-bezier(.34,1.56,.64,1) ${(swapAt + 0.3).toFixed(2)}s forwards`,
        }}
      >
        <div className="flex items-baseline gap-2">
          <span className="text-[13.5px] font-semibold tracking-[-0.01em] text-[#0b0f1a]">
            {t('home.story.itinerary.planTitle')}
          </span>
          <span className="ms-auto font-mono rtl:font-sans text-[9.5px] uppercase tracking-[0.07em] text-[#0e7490] rtl:normal-case rtl:tracking-normal">
            {t('home.story.itinerary.edit')}
          </span>
        </div>
        {['d1', 'd2', 'd3'].map((day, i) => (
          <div
            key={day}
            className={[
              'flex items-center gap-2.5 rounded-[10px] px-2.5 py-2',
              day === 'd2'
                ? 'border border-[#a9cdf6] bg-[rgba(37,99,235,.035)]'
                : 'border border-[#e7eaef] bg-white',
            ].join(' ')}
            style={{
              animation: `sxUp .45s cubic-bezier(.16,1,.3,1) ${(swapAt + 0.45 + i * 0.12).toFixed(2)}s backwards`,
            }}
          >
            <span aria-hidden="true" className="flex flex-none flex-col gap-[2.5px]">
              <span className="h-[1.5px] w-[9px] rounded-sm bg-[#ccd2dc]" />
              <span className="h-[1.5px] w-[9px] rounded-sm bg-[#ccd2dc]" />
            </span>
            <span className="flex-none font-mono text-[9.5px] text-[#6e6e77]">
              {day.toUpperCase()}
            </span>
            <span className="min-w-0 flex-1 truncate text-[12.5px] text-[#111827]">
              {t(`home.story.itinerary.${day}`)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
